import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { Modal, Input, Select, Button } from '../common';

const ExchangeRateForm = ({
  isOpen,
  onClose,
  onSubmit,
  currencies,
  loading
}) => {
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors }
  } = useForm({
    defaultValues: {
      fromCurrencyId: '',
      toCurrencyId: '',
      buyRate: '',
      sellRate: ''
    }
  });

  const fromCurrencyId = watch('fromCurrencyId');
  const toCurrencyId = watch('toCurrencyId');

  useEffect(() => {
    if (!isOpen) {
      reset({
        fromCurrencyId: '',
        toCurrencyId: '',
        buyRate: '',
        sellRate: ''
      });
    }
  }, [isOpen, reset]);

  const handleFormSubmit = (data) => {
    onSubmit({
      fromCurrencyId: parseInt(data.fromCurrencyId, 10),
      toCurrencyId: parseInt(data.toCurrencyId, 10),
      buyRate: parseFloat(data.buyRate),
      sellRate: parseFloat(data.sellRate)
    });
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const currencyOptions = currencies
    .filter(c => c.isActive)
    .map(c => ({
      value: c.id.toString(),
      label: `${c.code} - ${c.name}`
    }));

  const fromCurrencyOptions = currencyOptions.filter(
    option => option.value !== toCurrencyId
  );

  const toCurrencyOptions = currencyOptions.filter(
    option => option.value !== fromCurrencyId
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('currencies.setExchangeRate')}
      size="md"
    >
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
        <Select
          label={t('currencies.fromCurrency')}
          options={fromCurrencyOptions}
          placeholder={t('currencies.selectCurrency')}
          {...register('fromCurrencyId', {
            required: t('currencies.fromCurrencyRequired')
          })}
          error={errors.fromCurrencyId?.message}
        />

        <Select
          label={t('currencies.toCurrency')}
          options={toCurrencyOptions}
          placeholder={t('currencies.selectCurrency')}
          {...register('toCurrencyId', {
            required: t('currencies.toCurrencyRequired'),
            validate: (value) =>
              value !== fromCurrencyId || t('currencies.differentCurrencies')
          })}
          error={errors.toCurrencyId?.message}
        />

        <div className="grid grid-cols-2 gap-4">
          <Input
            label={t('currencies.buyRate')}
            type="number"
            step="0.0001"
            min="0"
            {...register('buyRate', {
              required: t('currencies.buyRateRequired'),
              min: {
                value: 0,
                message: t('currencies.ratePositive')
              },
              validate: (value) =>
                parseFloat(value) > 0 || t('currencies.ratePositive')
            })}
            error={errors.buyRate?.message}
            placeholder="0.0000"
          />

          <Input
            label={t('currencies.sellRate')}
            type="number"
            step="0.0001"
            min="0"
            {...register('sellRate', {
              required: t('currencies.sellRateRequired'),
              min: {
                value: 0,
                message: t('currencies.ratePositive')
              },
              validate: (value) =>
                parseFloat(value) > 0 || t('currencies.ratePositive')
            })}
            error={errors.sellRate?.message}
            placeholder="0.0000"
          />
        </div>

        <p className="text-sm text-gray-500">
          {t('currencies.rateExplanation')}
        </p>

        <div className="flex justify-end space-x-3 pt-4">
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={loading}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={loading}
          >
            {t('currencies.setRate')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default ExchangeRateForm;
